import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Pause, Play, Pencil, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { RIDE_TYPES, when } from "@/lib/rides";
import { ModifyDialog } from "@/components/rider/ModifyDialog";
import { RideCard } from "@/components/rider/RideCard";
import { DAY_NAMES, bucket, canModify, dayTime, useMyRides, useSeries } from "@/lib/rider";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const TABS = [["upcoming", "Upcoming"], ["active", "Active"], ["completed", "Completed"], ["cancelled", "Cancelled"], ["recurring", "Recurring"]];

function useSeriesAction(id) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const run = async (action, msg) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/rides/series/${id}/${action}`, {});
      toast.success(msg(data));
      ["mySeries", "myRides", "credit", "myProfile"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  return { busy, run };
}

function SeriesCard({ s, rides }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [sure, setSure] = useState(false);
  const { busy, run } = useSeriesAction(s.id);
  const mine = rides.filter((r) => r.series_id === s.id).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const next = mine.find((r) => r.id === s.next_ride_id);
  const editable = mine.find((r) => canModify(r));
  return (
    <article className="space-y-3 rounded-2xl border border-white/10 bg-card p-4" data-testid={`series-card-${s.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-primary">{s.trip_type === "split_trip" ? "Split Trip" : RIDE_TYPES[s.trip_type]} · Recurring</p>
          <p className="font-display text-lg font-bold" data-testid={`series-days-${s.id}`}>{s.days.map((d) => DAY_NAMES[d]).join(" · ")}</p>
          <p className="text-xs text-muted-foreground" data-testid={`series-times-${s.id}`}>{s.leg_times.map((t) => dayTime(t).time).join(" & ")}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${s.paused ? "bg-amber-400/10 text-amber-200" : s.status === "active" ? "bg-primary/15 text-primary" : "bg-white/10 text-white/60"}`}
          data-testid={`series-status-${s.id}`}>{s.paused ? "Paused" : s.status === "active" ? "Active" : "Ended"}</span>
      </div>
      {s.routes.map((r, i) => <p key={i} className="text-sm">{s.routes.length > 1 ? `Leg ${i + 1}: ` : ""}{r}</p>)}
      <p className="text-xs text-muted-foreground" data-testid={`series-next-${s.id}`}>
        {s.paused ? "Paused: rides are on hold until you resume" : s.next_at ? `Next ride ${when(s.next_at)}` : "No more upcoming rides"} · {s.upcoming} upcoming · {s.completed} done
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="ghost" className="h-8 rounded-full text-xs" onClick={() => setOpen((o) => !o)} data-testid={`series-view-${s.id}`}><CalendarDays className="mr-1 h-3 w-3" />View schedule</Button>
        {!s.paused && editable && <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => setEdit(true)} data-testid={`series-modify-${s.id}`}><Pencil className="mr-1 h-3 w-3" />Modify</Button>}
        {s.status === "active" && <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" disabled={!!busy} onClick={() => run("pause", (d) => `${d.held} rides on hold`)} data-testid={`series-pause-${s.id}`}><Pause className="mr-1 h-3 w-3" />Pause</Button>}
        {s.paused && <Button size="sm" className="h-8 rounded-full text-xs" disabled={!!busy} onClick={() => run("resume", (d) => `${d.restored} rides back on${d.skipped ? `, ${d.skipped} past dates skipped` : ""}`)} data-testid={`series-resume-${s.id}`}><Play className="mr-1 h-3 w-3" />Resume</Button>}
        {s.status !== "ended" && (sure ? (
          <Button size="sm" variant="destructive" className="h-8 rounded-full text-xs" disabled={!!busy} onClick={() => run("cancel", (d) => `${d.cancelled} rides cancelled`)} data-testid={`series-confirm-cancel-${s.id}`}>Cancel all upcoming</Button>
        ) : (
          <Button size="sm" variant="ghost" className="h-8 rounded-full text-xs text-muted-foreground" onClick={() => setSure(true)} data-testid={`series-cancel-${s.id}`}><XCircle className="mr-1 h-3 w-3" />Cancel</Button>
        ))}
        {busy && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
      </div>
      {open && <div className="space-y-2 border-t border-white/10 pt-3" data-testid={`series-schedule-${s.id}`}>{mine.map((r) => <RideCard key={r.id} ride={r} />)}</div>}
      {edit && <ModifyDialog ride={editable || next} open={edit} onOpenChange={setEdit} defaultScope="series" />}
    </article>
  );
}

export function MyRides({ tab, setTab, onFollow }) {
  const { data: rides, isLoading } = useMyRides();
  const { data: series } = useSeries();
  const all = rides || [];
  const counts = Object.fromEntries(TABS.map(([k]) => [k, k === "recurring" ? (series || []).length : all.filter((r) => bucket(r) === k).length]));
  const list = all.filter((r) => bucket(r) === tab).sort((a, b) => (tab === "upcoming" ? 1 : -1) * (new Date(a.scheduled_at) - new Date(b.scheduled_at)));
  return (
    <div className="space-y-4" data-testid="my-rides">
      <h1 className="font-display text-2xl font-bold tracking-tight">My rides</h1>
      <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1" data-testid="my-rides-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`rides-tab-${k}`}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${tab === k ? "border-primary bg-primary text-primary-foreground" : "border-white/15 text-muted-foreground hover:text-foreground"}`}>
            {label} <span className="opacity-70">{counts[k]}</span>
          </button>
        ))}
      </div>
      {isLoading && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
      {tab === "recurring" ? (
        <section className="space-y-3" data-testid="my-recurring-rides">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">My recurring rides</h2>
          {!series?.length && <p className="text-sm text-muted-foreground">No recurring schedules yet. Turn on "Repeat this ride?" when you book a scheduled ride.</p>}
          {series?.map((s) => <SeriesCard key={s.id} s={s} rides={all} />)}
        </section>
      ) : (
        <>
          {!list.length && !isLoading && <p className="text-sm text-muted-foreground" data-testid="rides-empty">Nothing here yet.</p>}
          {list.map((r) => <RideCard key={r.id} ride={r} onFollow={onFollow} />)}
        </>
      )}
    </div>
  );
}
